<template>
  <!-- Popout enabled -->
  <v-layout column>
    <!-- [Copied] File dialog -->
    <v-dialog
      max-width="800px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="selectOptionsDialogOpened"
    >
      <v-card v-if="documentUploading">
        <v-card-text>
          <v-layout column align-center justify-center class="ma-4">
            <p class="subheading">Uploading documents. Please wait...</p>
            <v-progress-circular
              indeterminate
              size="50"
              class="primary--text"
            />
          </v-layout>
        </v-card-text>
      </v-card>

      <v-card v-else-if="currentDocument">
        <v-card-title>
          <h2>Tell us about the document you just uploaded</h2>
        </v-card-title>

        <v-card-text class="text-xs-left pt-0">
          <p class="subheading">
            What is <b>{{ currentDocument.data.name }}</b> about? Please select
            all that apply.
          </p>

          <v-layout
            column
            v-for="question in allDocumentsNeeded"
            :key="question.id"
            class="option-group"
            v-if="question.options.length > 0"
          >
            <v-checkbox
              v-for="option in question.options"
              v-model="selectedOptions"
              :label="option"
              :key="option"
              :value="option"
              small
              solo
              hide-details
            />
          </v-layout>

          <v-layout column class="option-group">
            <v-checkbox
              v-model="selectedOtherOptions"
              label="Others"
              small
              solo
              hide-details
              class="mb-1"
            />

            <v-layout
              v-if="selectedOtherOptions"
              v-for="(option, index) in otherOptions"
              :key="option"
              align-center
              class="mt-2"
            >
              <v-text-field
                class="mx-4 my-0 default-input"
                label="Enter document name"
                v-model="otherOptions[index]"
                solo
                hide-details
              />

              <v-btn
                flat
                icon
                color="error"
                class="my-0"
                @click="deleteOtherOption(index)"
              >
                <v-icon class="material-icons-outlined">delete</v-icon>
              </v-btn>
            </v-layout>

            <v-layout v-if="selectedOtherOptions" class="mt-1">
              <v-btn
                outline
                color="primary"
                small
                class="mx-4 text-none"
                @click="addMoreOptions()"
              >
                <v-icon>add</v-icon>
                Add more row
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            class="error--text text-none"
            flat
            @click="selectOptionsDialogOpened = false"
          >
            Cancel
          </v-btn>

          <v-btn
            class="primary text-none"
            @click="eventEmitter.emit('selected-options')"
            :disabled="!canInsert"
          >
            Done
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <input
      type="file"
      style="display: none"
      ref="selectedDocuments"
      multiple
      @change="onDocumentsPicked"
    />

    <!-- Has items -->
    <aside
      class="document-checklist"
      @click="isChecklistOpen = !isChecklistOpen"
    >
      <div>
        <!-- Upload -->
        <div
          v-if="isChecklistOpen"
          slot="upload"
          class="document-checklist-popout"
          @click.stop.prevent
        >
          <!-- Drag and drop box -->
          <section
            class="documents-checklist__upload"
            v-if="!bulkUploadEnabled && !checklistLoading"
            @click="pickDocuments"
            @drop.prevent="addFile"
            @dragover.prevent
          >
            <v-layout v-if="documentUploading">
              <v-progress-circular
                indeterminate
                size="30"
                color="primary"
              ></v-progress-circular>
            </v-layout>
            <div v-else>
              <sx-flex :is-column="true" :gap="6">
                <sx-icon name="file-upload" :size="24"></sx-icon>
                <sx-flex :is-column="true">
                  <div style="text-align: left !important">
                    <sx-small>
                      Drag and drop files here or
                      <b class="clickable primary--text">browse</b>
                    </sx-small>
                  </div>
                  <sx-tiny color="light-gray">Max Size 150MB</sx-tiny>
                </sx-flex>
              </sx-flex>
            </div>
          </section>

          <sx-h6>Uploaded documents</sx-h6>

          <!-- Loading -->
          <v-layout v-if="checklistLoading">
            <v-progress-circular
              indeterminate
              size="40"
              color="primary"
            ></v-progress-circular>
          </v-layout>

          <!-- Choose files -->
          <v-layout v-else column>
            <!-- Empty -->
            <v-layout
              column
              v-if="
                isEmpty(currentQuestionnaireOtherDocuments) &&
                  !documentUploading
              "
              class="documents-checklist__empty"
            >
              <sx-small color="currentcolor"
                >No documents have been uploaded yet.</sx-small
              >
            </v-layout>
            <!-- Files uploaded -->
            <div v-else class="document-checklist__itemContainer">
              <div
                v-for="(document, i) in currentQuestionnaireOtherDocuments"
                @click="deleteExtractedDocument(document.id)"
              >
                <sx-document-checklist-item
                  :key="i"
                  :fileName="document.name"
                />
              </div>
            </div>
          </v-layout>

          <sx-h6 v-if="!isEmpty(allDocumentsNeeded)">Document checklist</sx-h6>

          <!-- List of required documents -->
          <div class="document-checklist__itemContainer">
            <sx-document-checklist-file
              v-for="question in allDocumentsNeeded"
              :key="question.id"
            >
              <!-- File: Name -->
              <sx-flex
                :gap="8"
                class="document-checklist__filename"
                :class="
                  !isEmpty(question.answer)
                    ? 'document-checklist__filename--checked'
                    : ''
                "
              >
                <div v-if="!isEmpty(question.answer)">
                  <sx-icon
                    :size="20"
                    name="check-circle"
                    color="currentcolor"
                  />
                </div>
                <div v-else>
                  <sx-icon :size="20" name="circle" />
                </div>
                <sc-flex :gap="8" :is-column="true">
                  <sc-small class="document-checklist__name">
                    <sx-html
                      :text="
                        !isEmpty(question.options)
                          ? question.options.join(', ')
                          : question.text
                      "
                    >
                    </sx-html>
                  </sc-small>
                  <div class="document-checklist__prior-toolbar">
                    <v-tooltip
                      bottom
                      v-if="!question.section === 'Documents from Last Year'"
                    >
                      <v-icon class="primary--text" slot="activator" size="18"
                        >help</v-icon
                      >
                      <span>You uploaded this last year</span>
                    </v-tooltip>
                    <v-tooltip bottom v-if="question.follow_up_question_id">
                      <v-icon class="primary--text" slot="activator" size="18"
                        >help</v-icon
                      >
                      <span
                        v-html="
                          formatReason(
                            question,
                            getParentQuestion(findSection(question), question),
                          )
                        "
                      />
                    </v-tooltip>
                    <v-tooltip bottom v-if="filteredComments(question).length">
                      <v-chip
                        slot="activator"
                        class="clickable-chip"
                        small
                        @click="$emit('open-comment-dialog', question)"
                        >{{ filteredComments(question).length }} Note{{
                          filteredComments(question).length > 1 ? 's' : ''
                        }}</v-chip
                      >
                      <span>Click to view notes</span>
                    </v-tooltip>
                  </div>
                </sc-flex>
              </sx-flex>
              <!-- File: Actions -->
              <div class="document-checkist__itemActions">
                <div
                  @click="
                    $emit('upload', question);
                    isChecklistOpen = false;
                  "
                >
                  <sc-button type="secondary" size="tiny" icon-left="upload">
                    Upload
                  </sc-button>
                </div>
                <div class="document-checklist__select">
                  <sc-button
                    size="tiny"
                    type="secondary"
                    icon-left="caret-down"
                  >
                    <span>{{ uploadShorts[question.answer] || 'Choose' }}</span>
                  </sc-button>
                  <select
                    @change="saveQuestion(question, $event)"
                    :value="question.answer"
                  >
                    <option disabled value="">Choose an option</option>
                    <option value="">Not provided</option>
                    <option
                      v-for="(option, i) in filteredUploadOptions(question)"
                      :value="option.value"
                      :key="i"
                    >
                      {{ option.text }}
                    </option>
                  </select>
                </div>
              </div>
            </sx-document-checklist-file>
          </div>
        </div>
      </div>
      <div
        :class="
          isEmpty(allDocumentsNeeded)
            ? 'document-checklist__bar document-checklist__bar--empty'
            : requiredDocumentCounts.requiredCount /
                requiredDocumentCounts.completedCount ===
              1
            ? 'document-checklist__bar document-checklist__bar--items-done'
            : 'document-checklist__bar document-checklist__bar--items'
        "
      >
        <div class="document-checklist__bar__names">
          <sx-icon name="cloud-upload" :size="22"></sx-icon>
          <sx-small color="currentcolor"><b>Document Uploads</b></sx-small>
        </div>
        <div class="document-checklist__indicator">
          <!-- Icon -->
          <sx-flex
            slot="icon"
            :is-y-centered="true"
            :gap="16"
            class="document-checklist__checkedbar"
            :is-between="true"
          >
            <div
              v-if="
                requiredDocumentCounts.requiredCount /
                  requiredDocumentCounts.completedCount ===
                  1
              "
            >
              <sx-icon name="check-circle" :size="20"></sx-icon>
            </div>
            <div v-else class="document-checklist__inlinebar">
              <sx-flex :is-column="true" :gap="2">
                <sx-tiny color="currentcolor"
                  >{{ requiredDocumentCounts.completedCount }}
                  <span>of</span>
                  {{ requiredDocumentCounts.requiredCount }}</sx-tiny
                >
                <progress
                  min="0"
                  max="100"
                  :value="
                    100 /
                      (requiredDocumentCounts.requiredCount /
                        requiredDocumentCounts.completedCount)
                  "
                />
              </sx-flex>
            </div>
            <div
              v-if="isChecklistOpen"
              class="document-checklist__inlinebar__indicator"
            >
              <sx-icon name="caret-down" :size="26" />
            </div>
            <div v-else class="document-checklist__inlinebar__indicator">
              <sx-icon name="caret-up" :size="26" />
            </div>
          </sx-flex>
        </div>
      </div>
    </aside>
  </v-layout>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';
import moment from 'moment';
import { predefinedQuestionAnswers } from '@/constants/variables';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';
import NewDocumentChecklistMixin from '@/mixins/NewDocumentChecklistMixin';
import SxButton from './sx/SxButton';
import SxButtonGroup from './sx/SxButtonGroup';
import SxDocumentChecklistFile from './sx/SxDocumentChecklistFile';
import SxDocumentChecklistItem from './sx/SxDocumentChecklistItem';
import SxFlex from './sx/SxFlex';
import SxH1 from './sx/SxH1';
import SxH2 from './sx/SxH2';
import SxH3 from './sx/SxH3';
import SxH4 from './sx/SxH4';
import SxH5 from './sx/SxH5';
import SxH6 from './sx/SxH6';
import SxIcon from './sx/SxIcon';
import SxSmall from './sx/SxSmall';
import SxTiny from './sx/SxTiny';
import SxHtml from './sx/SxHtml';
import API from '@/api';
import QuestionnaireMixin from '@/mixins/QuestionnaireMixin';

export default {
  components: {
    SxButton,
    SxButtonGroup,
    SxDocumentChecklistFile,
    SxDocumentChecklistItem,
    SxFlex,
    SxH1,
    SxH2,
    SxH3,
    SxH4,
    SxH5,
    SxH6,
    SxIcon,
    SxSmall,
    SxHtml,
    SxTiny,
  },
  props: {
    checklistLoading: {
      type: Boolean,
    },
    allDocuments: {
      type: Array,
      required: false,
    },
    currentQuestionnaire: {
      type: Object,
      default: null,
    },
    remainingItems: {
      type: Object,
      default: null,
    },
    selectedQuestion: {
      type: Object,
    },
    asyncForEach: {
      type: Function,
    },
    showUnanswered: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [FirmPreferenceMixins, NewDocumentChecklistMixin, QuestionnaireMixin],
  data() {
    return {
      isChecklistOpen: false,
      isBlurOpen: false,
      isDocumentUploading: false,
      predefinedQuestionAnswers,
      documentsToImport: [],
      documentUploading: false,
      uploadShorts: {
        'Provided in Questionnaire': 'Provided',
        'Provided Elsewhere': 'Provided',
        'Upload Later': 'Later',
        'Not Applicable': 'N/A',
      },
      uploadOptions: [
        {
          text: 'Provided Above',
          value: 'Provided in Questionnaire',
        },
        {
          text: 'Provided Elsewhere',
          value: 'Provided Elsewhere',
        },
        {
          text: 'Upload Later',
          value: 'Upload Later',
        },
        {
          text: 'Not Applicable',
          value: 'Not Applicable',
        },
      ],

      // Temporarily disable bulk upload
      // See: https://linear.app/soraban/issue/SOR-7064/disable-bulk-upload-in-document-popout
      bulkUploadEnabled: true,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),
    currentQuestionnaireOtherDocuments() {
      return this.currentQuestionnaire.extracted_documents || [];
    },
    areAllDocumentsNamed() {
      return this.documentsToImport.every(d => !isEmpty(d.name));
    },
    requiredDocumentCounts() {
      const requiredDocuments = this.allDocumentsNeeded;
      const requiredCount = requiredDocuments.length || 0;
      const completedCount =
        requiredDocuments.filter(rd => !!rd.answer).length || 0;

      return {
        requiredCount,
        completedCount,
      };
    },
  },

  methods: {
    isEmpty,
    moment,
    filteredUploadOptions(question) {
      let filters = [];
      if (question.disable_answer_later) {
        filters.push('Upload Later');
      }
      if (question.disable_not_applicable) {
        filters.push('Not Applicable');
      }
      if (this.disableProvidedElsewhere) {
        filters.push('Provided Elsewhere');
      }

      return this.uploadOptions.filter(a => !filters.includes(a.value));
    },

    listDocumentsWording(question) {
      return `${question.answer
        .split('&!&')
        .slice(1)
        .map(a => a.split('/')[a.split('/').length - 1])
        .join(', ')}`;
    },

    openRecognizedDocument(question) {
      this.$emit('preview-document', this.recognizedDocument(question));
    },

    recognizedDocument(question) {
      if (this.allDocuments) {
        return this.allDocuments.find(
          q =>
            q.detailed_name &&
            question.options
              .map(o => o.toLowerCase())
              .includes(q.detailed_name.toLowerCase()),
        );
      }
    },

    listDocuments(answer) {
      return answer
        .split('&!&')
        .filter((item, index) => !(index === 0 && item === ''));
    },

    formatReason(question, parentQuestion) {
      return `${this.currentUser.is_advisor ? 'Client' : 'You'} answered '${
        question.trigger_for_follow_up_question
      }' to<br/>'${parentQuestion.text}'`;
    },

    pickDocuments() {
      this.isChecklistOpen = false;
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },

    getExtensionOfFilename(filename) {
      return filename.split('.').pop();
    },

    getNameWithoutExtensionOfFilename(filename) {
      let splitName = filename.split('.');
      splitName.pop();
      return splitName.join('.');
    },

    async deleteExtractedDocument(id) {
      if (!confirm('Delete this file?')) return;

      try {
        const response = await API.extractedDocument.deleteExtractedDocument(
          id,
        );
        if (response.data.error) {
          console.log(response.data.error);
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Successfully deleted.',
          });

          if (
            this.currentQuestionnaire.extracted_documents &&
            this.currentQuestionnaire.extracted_documents.length
          ) {
            this.currentQuestionnaire.extracted_documents = this.currentQuestionnaire.extracted_documents.filter(
              d => d.id !== id,
            );
          }
        }
      } catch (e) {
        console.log(e);
      }
    },

    async addFile(e) {
      this.isChecklistOpen = false;
      const documents = e.dataTransfer.files;
      if (documents) {
        const documentType = this.selectedDocumentType;
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name: this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
            };
          }),
        );
        this.localUploadBulkDocument();
      }
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    saveQuestion(question, event) {
      const value =
        !!event.target && !!event.target.value ? event.target.value : undefined;

      const hasModalFlow = Boolean(
        value === 'Not Applicable' ||
          value === 'Provided Elsewhere' ||
          value === 'Upload Later' ||
          value === 'Answer Later',
      );

      // NOTE @chris If there is a second step involving a modal, we should hide the checklist.
      if (hasModalFlow) this.isChecklistOpen = false;

      if (value === 'Not Applicable') {
        this.$emit(
          'open-comment-dialog',
          question,
          'Please explain why this is not applicable or no longer applicable.',
          value,
        );
      } else if (value === 'Provided Elsewhere') {
        this.$emit(
          'open-comment-dialog',
          question,
          'Please explain where/how you provided this document (e.g. Email, Mail, In-person).',
          value,
        );
      } else if (value === 'Upload Later' || value === 'Answer Later') {
        this.$emit('open-answer-later-dialog', question);
      }
    },

    getIsNewBulkUpload() {
      const isNewMethod = !this.currentFirm.preferences[
        'old_document_checklist'
      ];

      return isNewMethod;
    },

    async onDocumentsPicked(e) {
      const documents = e.target.files;
      if (documents) {
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name: this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
            };
          }),
        );
        this.localUploadBulkDocument();
      }
    },

    async localUploadBulkDocument() {
      const isNewBulkUpload = this.getIsNewBulkUpload();
      if (isNewBulkUpload) return this.uploadBulkDocument();

      this.documentUploading = true;
      let path;
      if (isEmpty(this.currentQuestionnaire.document_path)) {
        path = '';
      } else {
        path = this.currentQuestionnaire.document_path
          .replace('{year}', this.currentQuestionnaire.tax_year)
          .replace('{section}', 'Bulk Uploads')
          .replace('{questionnaire_name}', this.currentQuestionnaire.name);
      }
      try {
        await this.asyncForEach(
          this.documentsToImport,
          async documentToImport => {
            const {
              data: { id, upload_link },
            } = await API.integration.getTemporaryUploadLink({
              companyId: this.currentQuestionnaire.company_id,
            });

            await API.integration.uploadToDropbox(
              upload_link,
              documentToImport.data,
            );

            const response = await API.integration.processDocument({
              id,
              documentName:
                documentToImport.name + '.' + documentToImport.extension,
              companyId: this.currentQuestionnaire.company_id,
              path: '/'.concat(path),
              questionnaireId: this.currentQuestionnaire.id,
            });

            if (response.data.error) {
              console.log(response.data.error);
            } else {
              await this.sleep(2000);
              this.$emit('refresh-questionnaire');
            }
          },
        );
        this.documentsToImport = [];
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem uploading the documents.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.documentUploading = false;
        // NOTE @chris We open the modal back up once the bulk upload is done to show the files.
        this.isChecklistOpen = true;
      }
    },
  },
};
</script>

<style lang="css" scoped>
.document-checklist {
  position: relative;
  z-index: 1000;
}

.document-checklist-popout {
  position: fixed;
  bottom: 0;
  left: 4px;
  z-index: 10;

  max-height: calc(100dvh - 80px);
  display: flex;
  gap: 12px;
  overflow: auto;
  padding: 16px 8px 64px 8px;
  flex-direction: column;
  max-width: 100%;
  /* opacity: 0; */
  width: 324px;

  /* animation: fadeIn 10ms ease-in-out 540ms forwards; */
  background-color: rgba(210, 220, 240, 0.85);
  backdrop-filter: blur(2px);
  border-radius: 8px 8px 0 0;
  /* transform: translateY(-16px); */
}

.document-checklist-popout::-webkit-scrollbar {
  height: 2px;
  width: 2px;
}

.document-checklist-popout::-webkit-scrollbar-thumb {
  height: 2px;
  width: 2px;

  background-color: var(--blue-300);
  transition: all 80ms ease-in-out;
}

.document-checklist-popout::-webkit-scrollbar-track {
  background-color: transparent;
}

.document-checklist-popout div > div:last-child a > div {
  margin-bottom: 0 !important;
}

.documents-checklist__empty {
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 16px;

  background-color: var(--gray-0);
  border: 1px solid var(--blue-200);
  border-radius: 6px;
  color: var(--gray-500);
}

.documents-checklist__upload {
  padding: 16px;

  background-color: var(--gray-0);
  /* box-shadow: 0px 1px 1px 0px rgba(114, 113, 125, 0.25); */
  border: 1px dashed var(--blue-300);
  border-radius: 6px;
  color: var(--gray-800);
  transition: all 80ms ease-in-out;

  cursor: pointer;
}

.documents-checklist__upload {
  color: var(--blue-500);
}

.document-checklist__itemContainer {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.documents-checklist__upload:hover {
  background-color: var(--blue-25);
}

.document-checkist__itemActions {
  position: relative;
  z-index: 12;

  display: flex;
  gap: 2px;
}

.document-checklist__select {
  position: relative;
}

.document-checklist__select select {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;

  height: 100%;
  opacity: 0.00001;
  width: 100%;

  cursor: pointer;
  outline: none;
}

.document-checklist__filename {
  position: relative;

  display: flex;
  width: 100%;

  color: var(--gray-200);
}

.document-checklist__filename small {
  overflow: hidden;

  text-align: left !important;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-checklist__filename svg {
  position: absolute;
  top: 10px;
  left: 2px;

  height: 18px;
  min-width: 18px;
  width: 18px;

  color: var(--gray-500);
}

.document-checklist__filename--checked {
  color: var(--green-600);
}

.document-checklist__inlinebar,
.document-checklist__checkedbar {
  width: 120px;

  color: var(--gray-0);
}

.document-checklist__bar--items-done .document-checklist__checkedbar {
  width: 100%;
}

.document-checklist__inlinebar span {
  display: inline-flex;
  padding: 0 3px;

  color: var(--blue-300);
}

.document-checklist__inlinebar progress {
  display: block;
  height: 4px;
  min-height: 4px;
  width: 100%;

  background-color: var(--gray-0);
  border: 0;
  border-radius: 24px;
}

.document-checklist__inlinebar progress::-webkit-progress-bar {
  background-color: var(--gray-0);
  border-radius: 24px;
}

.document-checklist__inlinebar progress::-webkit-progress-value {
  background-color: var(--blue-300);
  border-radius: 24px;
  transition: all 80ms ease-in-out;
}

.document-checklist {
  position: relative;

  display: flex;
  justify-content: space-between;

  user-select: none;
}

.document-checklist__indicator {
  align-items: center;
  justify-content: center;
  display: flex;

  color: var(--gray-0);
}

.document-checklist__inlinebar__indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  min-width: 48px;
  width: 48px;

  border-left: 1px solid rgba(0, 0, 0, 0.125);
}

.document-checklist__bar {
  position: fixed;
  bottom: 0;
  left: 4px;
  z-index: 11;

  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0 0 0 14px;
  height: 48px;
  width: 324px;

  border-top-right-radius: 4px;
  border-top-left-radius: 4px;
  transition: all 80ms ease-in-out;

  cursor: pointer;
}

.document-checklist__bar__names {
  display: flex;
  gap: 10px;
}
.document-checklist__bar__names > div {
  align-items: center;
}

.document-checklist__bar--items-done {
  background-color: var(--green-600);
  color: var(--gray-0);
}

.document-checklist__bar--items {
  background: linear-gradient(var(--blue-500), var(--blue-600));
  color: var(--gray-0);
}

.document-checklist__bar--items:hover:before {
  opacity: 1;
}

.document-checklist__bar--items:before {
  position: absolute;
  left: 0;
  z-index: -1;

  height: 100%;
  opacity: 0;
  width: 100%;

  background: linear-gradient(var(--blue-600), var(--blue-600));
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  transition: all 120ms ease-in-out;

  content: ' ';
}

.document-checklist__bar--empty {
  background-color: var(--gray-100);

  border-top: 1px solid var(--gray-200);
  border-right: 1px solid var(--gray-200);
  box-shadow: inset 0 1px 0 var(--gray-50);
  color: var(--gray-800);
}

.document-checklist__bar--empty .document-checklist__indicator {
  display: none;
}

.document-checklist__prior-toolbar {
  position: relative;
  z-index: 1002;

  align-items: end;
  display: flex;
  width: 100%;
}

.document-checklist__name {
  color: var(--gray-700);

  line-height: 20px;
}
</style>
